import { createContext, useContext, ReactNode } from "react";
import { createClient, SupabaseClient } from "@supabase/supabase-js";
import { config } from "../config/env";

// Define the shape of our database schema (extended for Todo List Templates)
interface Database {
  public: {
    Tables: {
      // Existing tables
      posts: {
        Row: {
          id: string;
          title: string;
          content: string;
          user_id: string;
          created_at: string;
        };
        Insert: {
          id?: string;
          title: string;
          content: string;
          user_id: string;
          created_at?: string;
        };
        Update: {
          id?: string;
          title?: string;
          content?: string;
          user_id?: string;
          created_at?: string;
        };
      };
      comments: {
        Row: {
          id: string;
          post_id: string;
          user_id: string;
          content: string;
          created_at: string;
        };
        Insert: {
          id?: string;
          post_id: string;
          user_id: string;
          content: string;
          created_at?: string;
        };
        Update: {
          id?: string;
          post_id?: string;
          user_id?: string;
          content?: string;
          created_at?: string;
        };
      };
      // New tables for Todo List Templates
      categories: {
        Row: {
          id: string;
          name: string;
          description: string | null;
          color: string | null;
          icon: string | null;
          user_id: string;
          is_public: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          name: string;
          description?: string | null;
          color?: string | null;
          icon?: string | null;
          user_id: string;
          is_public?: boolean;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          name?: string;
          description?: string | null;
          color?: string | null;
          icon?: string | null;
          user_id?: string;
          is_public?: boolean;
          updated_at?: string;
        };
      };
      todo_templates: {
        Row: {
          id: string;
          name: string;
          description: string | null;
          category_id: string;
          user_id: string;
          is_public: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          name: string;
          description?: string | null;
          category_id: string;
          user_id: string;
          is_public?: boolean;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          name?: string;
          description?: string | null;
          category_id?: string;
          user_id?: string;
          is_public?: boolean;
          updated_at?: string;
        };
      };
      template_items: {
        Row: {
          id: string;
          template_id: string;
          title: string;
          description: string | null;
          order_index: number;
          is_completed: boolean;
          priority: "low" | "medium" | "high";
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          template_id: string;
          title: string;
          description?: string | null;
          order_index: number;
          is_completed?: boolean;
          priority?: "low" | "medium" | "high";
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          template_id?: string;
          title?: string;
          description?: string | null;
          order_index?: number;
          is_completed?: boolean;
          priority?: "low" | "medium" | "high";
          updated_at?: string;
        };
      };
      user_todo_lists: {
        Row: {
          id: string;
          name: string;
          description: string | null;
          template_id: string | null;
          user_id: string;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          name: string;
          description?: string | null;
          template_id?: string | null;
          user_id: string;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          name?: string;
          description?: string | null;
          template_id?: string | null;
          user_id?: string;
          updated_at?: string;
        };
      };
      user_todo_items: {
        Row: {
          id: string;
          list_id: string;
          title: string;
          description: string | null;
          order_index: number;
          is_completed: boolean;
          priority: "low" | "medium" | "high";
          due_date: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          list_id: string;
          title: string;
          description?: string | null;
          order_index: number;
          is_completed?: boolean;
          priority?: "low" | "medium" | "high";
          due_date?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          list_id?: string;
          title?: string;
          description?: string | null;
          order_index?: number;
          is_completed?: boolean;
          priority?: "low" | "medium" | "high";
          due_date?: string | null;
          updated_at?: string;
        };
      };
    };
  };
}

type TypedSupabaseClient = SupabaseClient<Database>;

interface SupabaseProviderProps {
  children: ReactNode;
}

const SupabaseContext = createContext<TypedSupabaseClient | null>(null);

export const SupabaseProvider = ({ children }: SupabaseProviderProps) => {
  // Use centralized environment configuration
  const supabase = createClient<Database>(
    config.supabase.url,
    config.supabase.anonKey
  );

  return (
    <SupabaseContext.Provider value={supabase}>
      {children}
    </SupabaseContext.Provider>
  );
};

export const useSupabase = (): TypedSupabaseClient => {
  const context = useContext(SupabaseContext);

  if (!context) {
    throw new Error("useSupabase must be used within a SupabaseProvider");
  }

  return context;
};
